import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type MessageKey, useT } from "@trust/i18n";
import { Card, Empty, Space, Table, Tabs, Tag, Typography, type TableProps } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { listDoneTasks, listTodoTasks, type TaskItem } from "./api";

// P1.9 任务中心:替代旧 Camunda 待办/已办任务列表。
// 待办/已办的角色语义由后端 services/api/.../routers/tasks.py 决定(admin 审核 / expert 评审 / applicant 整改)。

type Bucket = "todo" | "done";
type Row = TaskItem & { bucket: Bucket };

const KIND_TAG: Record<TaskItem["kind"], { labelKey: MessageKey; color: string }> = {
  guide: { labelKey: "admin.tasks.kindGuide", color: "blue" },
  evaluation: { labelKey: "admin.tasks.kindEvaluation", color: "purple" },
  flow_log: { labelKey: "admin.tasks.kindFlowLog", color: "default" },
};

export function TasksPage() {
  const t = useT();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"all" | Bucket>("all");

  const todoQ = useQuery({ queryKey: ["tasks", "todo"], queryFn: listTodoTasks });
  const doneQ = useQuery({ queryKey: ["tasks", "done"], queryFn: listDoneTasks });

  const rows = useMemo<Row[]>(() => {
    const todo = (todoQ.data ?? []).map((i): Row => ({ ...i, bucket: "todo" }));
    const done = (doneQ.data ?? []).map((i): Row => ({ ...i, bucket: "done" }));
    const byTimeDesc = (a: Row, b: Row) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf();
    if (tab === "todo") return todo.sort(byTimeDesc);
    if (tab === "done") return done.sort(byTimeDesc);
    return [...todo, ...done].sort(byTimeDesc);
  }, [todoQ.data, doneQ.data, tab]);

  const go = (row: TaskItem) => {
    if (row.guide_id == null) return;
    navigate({ to: "/inx/guide/$guideId", params: { guideId: String(row.guide_id) } });
  };

  const tEnum = (ns: "status" | "stage" | "operation", v: string | null) =>
    v ? t(`admin.tasks.${ns}.${v}` as MessageKey) : "-";

  const columns: TableProps<Row>["columns"] = [
    {
      title: t("admin.tasks.colType"),
      key: "kind",
      width: 170,
      render: (_, row) => (
        <Space size={4}>
          <Tag color={KIND_TAG[row.kind].color}>{t(KIND_TAG[row.kind].labelKey)}</Tag>
          <Tag color={row.bucket === "todo" ? "orange" : "green"}>
            {t(row.bucket === "todo" ? "admin.tasks.bucketTodo" : "admin.tasks.bucketDone")}
          </Tag>
        </Space>
      ),
    },
    {
      title: t("admin.tasks.colTitle"),
      dataIndex: "title",
      key: "title",
      render: (v: string, row) => (
        <div>
          {row.guide_id != null ? (
            <Typography.Link onClick={() => go(row)}>{v}</Typography.Link>
          ) : (
            v
          )}
          {row.comment && <div style={{ fontSize: 12, color: "#888" }}>{row.comment}</div>}
        </div>
      ),
    },
    {
      title: t("admin.tasks.colStatus"),
      key: "status",
      width: 170,
      render: (_, row) => (
        <Space size={4}>
          {row.stage && <Tag>{tEnum("stage", row.stage)}</Tag>}
          {row.status && <Tag color="processing">{tEnum("status", row.status)}</Tag>}
        </Space>
      ),
    },
    {
      title: t("admin.tasks.colOperation"),
      dataIndex: "operation",
      key: "operation",
      width: 120,
      render: (v: string | null) => tEnum("operation", v),
    },
    {
      title: t("admin.tasks.colTime"),
      dataIndex: "time",
      key: "time",
      width: 150,
      render: (v: string) => dayjs(v).format("YYYY-MM-DD HH:mm"),
    },
  ];

  const emptyText = t(
    tab === "todo"
      ? "admin.tasks.emptyTodo"
      : tab === "done"
        ? "admin.tasks.emptyDone"
        : "admin.tasks.emptyAll",
  );

  return (
    <Card title={t("admin.tasks.title")} size="small">
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as "all" | Bucket)}
        items={[
          {
            key: "all",
            label: `${t("admin.tasks.tabAll")} (${(todoQ.data?.length ?? 0) + (doneQ.data?.length ?? 0)})`,
          },
          { key: "todo", label: `${t("admin.tasks.tabTodo")} (${todoQ.data?.length ?? 0})` },
          { key: "done", label: `${t("admin.tasks.tabDone")} (${doneQ.data?.length ?? 0})` },
        ]}
      />
      <Table
        size="small"
        rowKey={(r) => `${r.bucket}-${r.kind}-${r.ref_id}`}
        loading={todoQ.isLoading || doneQ.isLoading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty description={emptyText} /> }}
        onRow={(row) => ({
          onClick: () => go(row),
          style: { cursor: row.guide_id != null ? "pointer" : "default" },
        })}
      />
    </Card>
  );
}
